import { useEffect, useState } from "react";
import TeamMemberForm from "../../components/TeamMemberForm";
import CoreLayout from "../../components/layout/CoreLayout";
import PageContainer from "../../components/PageContainer";
import PageHeader from "../../components/PageHeader";
import { cardBorderClassName } from "../../lib/cardStyles";
import {
  loadTeamMembers,
  memberInitials,
  saveTeamMembers,
  type TeamMember,
} from "../../lib/teamMembers";
import { usePageTitle } from "../../lib/usePageTitle";

// TODO: wire to real team/users API post-launch

function createMemberId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function Team() {
  usePageTitle("Team");
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    setMembers(loadTeamMembers());
  }, []);

  const persist = (next: TeamMember[]) => {
    setMembers(next);
    saveTeamMembers(next);
  };

  const addMember = (fields: {
    name: string;
    role: string;
    phone: string;
    email: string;
  }) => {
    const member: TeamMember = {
      id: createMemberId(),
      ...fields,
    };
    persist([member, ...members]);
    setShowForm(false);
  };

  const deleteMember = (id: string) => {
    persist(members.filter((m) => m.id !== id));
  };

  return (
    <CoreLayout>
      <PageContainer>
          <PageHeader
            title="Team"
            subtitle="Manage who handles follow-ups and transfer routing."
            actions={
              !showForm && (
                <button
                  type="button"
                  onClick={() => setShowForm(true)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer shrink-0"
                  style={{
                    background: "linear-gradient(90deg, #8a2be2, #ff6a00)",
                    fontFamily: "'Space Grotesk', sans-serif",
                  }}
                >
                  Add Member
                </button>
              )
            }
          />

          {showForm && (
            <TeamMemberForm
              className="mb-8"
              onSubmit={addMember}
              onCancel={() => setShowForm(false)}
            />
          )}

          {members.length === 0 ? (
            <div className="text-center py-16">
              <i className="ri-team-line text-5xl text-purple-400/30 mb-5 block" />
              <p
                className="text-gray-400"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                No team members yet. Add your first team member.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {members.map((member) => (
                <div
                  key={member.id}
                  className={`relative rounded-xl p-5 ${cardBorderClassName}`}
                  style={{
                    background: "rgba(28, 28, 37, 0.8)",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => deleteMember(member.id)}
                    className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-500 hover:text-red-400 transition-colors cursor-pointer"
                    style={{ background: "rgba(255,255,255,0.04)" }}
                    aria-label={`Remove ${member.name}`}
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>

                  <div className="flex items-start gap-4 pr-8">
                    <div
                      className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold text-white"
                      style={{ background: "linear-gradient(135deg, #8a2be2, #ff6a00)" }}
                    >
                      {memberInitials(member.name)}
                    </div>
                    <div className="min-w-0">
                      <p
                        className="text-white font-semibold"
                        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                      >
                        {member.name}
                      </p>
                      {member.role && (
                        <p
                          className="text-gray-400 text-sm mt-0.5"
                          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                          {member.role}
                        </p>
                      )}
                      {member.phone && (
                        <p className="text-xs font-mono text-orange-400 mt-2">{member.phone}</p>
                      )}
                      {member.email && (
                        <p className="text-xs font-mono text-orange-400 mt-0.5">{member.email}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </PageContainer>
    </CoreLayout>
  );
}
